import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getOrder,
  updateOrderStatus,
  retryOrder,
  archiveOrder,
  getOrderVideoUrl,
  getOrderComponentReports,
} from '../../services/orderService'
import type { OrderDetailResponse, ComponentReport, ConditionState } from '../../types'
import OrderStatusBadge, { getNextStatus } from '../../components/ui/OrderStatusBadge'
import './OrderDetailPage.css'

export default function OrderDetailPage() {
  const { orderNumber } = useParams<{ orderNumber: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [order, setOrder] = useState<OrderDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoOpen, setVideoOpen] = useState(false)
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)
  const [componentReports, setComponentReports] = useState<ComponentReport[] | null>(null)

  const load = useCallback(async () => {
    if (!orderNumber) return
    try {
      const data = await getOrder(orderNumber)
      setOrder(data)
      getOrderComponentReports(data.id)
        .then(setComponentReports)
        .catch(() => {/* report not yet available */})
    } catch (err) {
      console.error('[ERROR] Failed to load order detail', err)
      showToast('Failed to load order', 'error')
    } finally {
      setLoading(false)
    }
  }, [orderNumber])

  useEffect(() => { load() }, [load])

  function showToast(msg: string, type: 'success' | 'error') {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  async function handleStatusAdvance() {
    if (!order) return
    const next = getNextStatus(order.status)
    if (!next) return
    setActionLoading(true)
    try {
      await updateOrderStatus(order.orderNumber, next)
      await load()
      showToast(t('admin.order_detail.status_updated', { status: next }), 'success')
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'ORDER_ALREADY_FINALIZED') {
        await load()
        showToast(t('admin.order_detail.already_finalized'), 'error')
      } else {
        showToast(t('admin.order_detail.update_status_failed'), 'error')
      }
    } finally {
      setActionLoading(false)
    }
  }

  async function handleRetry() {
    if (!order) return
    setActionLoading(true)
    try {
      const result = await retryOrder(order.orderNumber)
      await load()
      showToast(t('admin.order_detail.retry_ai', { count: result.retryCount }), 'success')
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'RETRY_LIMIT_EXCEEDED') showToast(t('admin.order_detail.retry_limit_exceeded'), 'error')
      else if (code === 'ORDER_ALREADY_PROCESSING') showToast(t('admin.order_detail.already_processing'), 'error')
      else if (code === 'VIDEO_FILE_NOT_FOUND') showToast(t('admin.order_detail.video_not_found'), 'error')
      else showToast(t('admin.order_detail.retry_failed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleArchive() {
    if (!order || !confirm(t('admin.order_detail.archive_confirm'))) return
    setActionLoading(true)
    try {
      await archiveOrder(order.orderNumber)
      showToast(t('admin.order_detail.order_archived'), 'success')
      setTimeout(() => navigate('/admin/orders'), 1000)
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      if (code === 'ORDER_PROCESSING') showToast(t('admin.order_detail.order_processing'), 'error')
      else showToast(t('admin.order_detail.archive_failed'), 'error')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleOpenVideo() {
    if (!orderNumber) return
    try {
      const url = await getOrderVideoUrl(orderNumber)
      setVideoUrl(url)
      setVideoOpen(true)
    } catch {
      showToast(t('admin.order_detail.video_load_failed'), 'error')
    }
  }

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}><div className="admin-spinner" /></div>
  }

  if (!order) {
    return <div style={{ color: '#5a5d72', padding: 32 }}>{t('admin.order_detail.not_found')}</div>
  }

  const isArchived = !!order.archivedAt
  const nextStatus = getNextStatus(order.status)
  const canRetry = order.status === 'AI_ERROR' && order.retryCount < 4
  const retryLimitReached = order.retryCount >= 4

  return (
    <div className="order-detail">
      {/* Header */}
      <div className="admin-page-header">
        <button className="admin-back-btn" onClick={() => navigate('/admin/orders')}>
          <BackIcon />
        </button>
        <h1 className="order-detail__title">{order.orderNumber}</h1>
        <OrderStatusBadge status={order.status} />
        {isArchived && <span className="order-detail__archived-badge">{t('admin.order_detail.archived_badge')}</span>}
      </div>

      {/* Actions */}
      {!isArchived && (
        <div className="order-detail__actions">
          {nextStatus && (
            <button
              className="admin-btn admin-btn--primary"
              onClick={handleStatusAdvance}
              disabled={actionLoading}
            >
              {t('admin.order_detail.move_to', { status: nextStatus.replace('_', ' ') })}
            </button>
          )}
          {canRetry && (
            <button
              className="admin-btn admin-btn--warn"
              onClick={handleRetry}
              disabled={actionLoading}
            >
              {t('admin.order_detail.retry_ai', { count: order.retryCount })}
            </button>
          )}
          {retryLimitReached && order.status === 'AI_ERROR' && (
            <span className="order-detail__retry-limit">{t('admin.order_detail.retry_limit')}</span>
          )}
          <button
            className="admin-btn admin-btn--danger"
            onClick={handleArchive}
            disabled={actionLoading || order.status === 'AI_PROCESSING'}
            title={order.status === 'AI_PROCESSING' ? t('admin.order_detail.cannot_archive_processing') : ''}
          >
            {t('admin.order_detail.archive')}
          </button>
        </div>
      )}

      {/* Vehicle & Client */}
      <div className="order-detail__grid-2">
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.order_detail.card_vehicle')}</p>
          <div className="admin-detail-grid">
            <Field label={t('admin.order_detail.field_model')} value={order.carModel} />
            <Field label={t('admin.order_detail.field_year')} value={order.carYear} />
            <Field label={t('admin.order_detail.field_vin')} value={order.vin} />
            <Field label={t('admin.order_detail.field_plate')} value={order.carNumber} />
            <Field label={t('admin.order_detail.field_mileage')} value={order.carMileage ? `${order.carMileage} km` : '—'} />
          </div>
        </div>
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.order_detail.card_client')}</p>
          <div className="admin-detail-grid">
            <Field label={t('admin.order_detail.field_client')} value={order.clientName} />
            <Field label={t('admin.order_detail.field_phone')} value={order.clientPhone} />
            <Field label={t('admin.order_detail.field_mechanic')} value={order.mechanicName} />
            <Field label={t('admin.order_detail.field_date')} value={formatDate(order.dateTimeTs)} />
          </div>
        </div>
      </div>

      {/* AI Jobs */}
      <div className="admin-detail-card">
        <p className="admin-detail-card__title">{t('admin.order_detail.card_ai')}</p>
        <div className="admin-detail-grid">
          <Field label={t('admin.order_detail.field_yolo_status')} value={order.yoloStatus} highlight={order.yoloStatus === 'FAILED' ? 'error' : order.yoloStatus === 'SUCCESS' ? 'ok' : undefined} />
          <Field label={t('admin.order_detail.field_tts_status')} value={order.ttsStatus} highlight={order.ttsStatus === 'FAILED' ? 'error' : order.ttsStatus === 'SUCCESS' ? 'ok' : undefined} />
          <Field label={t('admin.order_detail.field_retries')} value={`${order.retryCount} / 4`} />
          {order.lastRetryAt && <Field label={t('admin.order_detail.field_last_retry')} value={formatDate(order.lastRetryAt)} />}
          {order.yoloErrorReason && <Field label={t('admin.order_detail.field_error_reason')} value={order.yoloErrorReason} highlight="error" />}
          {order.yoloSubmittedAt && <Field label={t('admin.order_detail.field_yolo_submitted')} value={formatDate(order.yoloSubmittedAt)} />}
          {order.yoloCompletedAt && <Field label={t('admin.order_detail.field_yolo_completed')} value={formatDate(order.yoloCompletedAt)} />}
          {order.ttsSubmittedAt && <Field label={t('admin.order_detail.field_tts_submitted')} value={formatDate(order.ttsSubmittedAt)} />}
          {order.ttsCompletedAt && <Field label={t('admin.order_detail.field_tts_completed')} value={formatDate(order.ttsCompletedAt)} />}
        </div>
      </div>

      {/* Summary */}
      {order.summary && (
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.order_detail.card_summary')}</p>

          {order.summary.speechText && (
            <>
              <p className="admin-detail-card__subtitle">{t('admin.order_detail.subtitle_speech')}</p>
              <p className="order-detail__summary-text">{order.summary.speechText}</p>
            </>
          )}

          <p className="admin-detail-card__subtitle">{t('admin.order_detail.subtitle_summary')}</p>
          <p className="order-detail__summary-text">{order.summary.summary}</p>

          {order.summary.aiAssistantSpeechText && (
            <>
              <p className="admin-detail-card__subtitle">{t('admin.order_detail.subtitle_ai_speech')}</p>
              <p className="order-detail__summary-text">{order.summary.aiAssistantSpeechText}</p>
            </>
          )}

          <p className="admin-detail-card__subtitle">{t('admin.order_detail.subtitle_audio')}</p>
          <audio
            controls
            src={audioUrl(order.orderNumber)}
            className="order-detail__audio"
          />
        </div>
      )}

      {/* Video */}
      <div className="admin-detail-card">
        <p className="admin-detail-card__title">{t('admin.order_detail.card_video')}</p>
        <button className="admin-btn admin-btn--secondary order-detail__video-btn" onClick={handleOpenVideo}>
          <PlayIcon /> {t('admin.order_detail.play_video')}
        </button>
      </div>

      {/* Frames */}
      {order.frames && order.frames.length > 0 && (
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.order_detail.card_frames', { count: order.frames.length })}</p>
          <div className="order-detail__frames">
            {order.frames.map((f) => {
              const url = frameUrl(f.s3Key)
              return (
                <img
                  key={f.frameIndex}
                  className="order-detail__frame"
                  src={url}
                  alt={`Frame ${f.frameIndex}`}
                  title={`${f.timestampSeconds.toFixed(2)}s`}
                  onClick={() => setSelectedFrame(url)}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Diagnostic report */}
      {componentReports && componentReports.length > 0 && (
        <DiagnosticReport reports={componentReports} />
      )}

      {/* YOLO detections */}
      {order.yoloDetections && order.yoloDetections.length > 0 && (
        <div className="admin-detail-card">
          <p className="admin-detail-card__title">{t('admin.order_detail.card_yolo', { count: order.yoloDetections.length })}</p>
          {Object.entries(
            order.yoloDetections.reduce<Record<string, typeof order.yoloDetections>>((acc, d) => {
              ;(acc[d.componentId] ??= []).push(d)
              return acc
            }, {})
          ).map(([componentId, detections]) => (
            <div key={componentId} className="order-detail__yolo-group">
              <p className="admin-detail-card__subtitle">{componentId} ({detections.length})</p>
              <div className="order-detail__yolo-scroll">
                {detections.map((d, i) => (
                  <div
                    key={i}
                    className="order-detail__yolo-item"
                    onClick={() => setSelectedFrame(d.frameHttpUrl)}
                  >
                    <img
                      src={d.frameHttpUrl}
                      alt={`${componentId} ${i + 1}`}
                      className="order-detail__yolo-img"
                    />
                    <span className={`order-detail__yolo-badge${d.confidence < 0.7 ? ' order-detail__yolo-badge--low' : ''}`}>
                      {(d.confidence * 100).toFixed(0)}%
                    </span>
                    <span className="order-detail__yolo-ts">{d.timestampSec.toFixed(2)}s</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video modal */}
      {videoOpen && videoUrl && (
        <div className="order-detail__video-overlay" onClick={() => setVideoOpen(false)}>
          <div className="order-detail__video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="order-detail__video-close" onClick={() => setVideoOpen(false)}>×</button>
            <video src={videoUrl} controls autoPlay className="order-detail__video-player">
              {t('admin.order_detail.no_video_support')}
            </video>
          </div>
        </div>
      )}

      {/* Frame lightbox */}
      {selectedFrame && (
        <div className="order-detail__video-overlay" onClick={() => setSelectedFrame(null)}>
          <img
            src={selectedFrame}
            className="order-detail__lightbox-img"
            onClick={(e) => e.stopPropagation()}
            alt="Frame preview"
          />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`admin-toast admin-toast--${toast.type}`}>
          {toast.msg}
        </div>
      )}
    </div>
  )
}

const CONDITION_COLORS: Record<ConditionState, string> = {
  CRITICAL: '#ef4444',
  MINOR:    '#f59e0b',
  NORMAL:   '#10b981',
}

function DiagnosticReport({ reports }: { reports: ComponentReport[] }) {
  const { t } = useTranslation()
  const grouped: Record<ConditionState, ComponentReport[]> = {
    CRITICAL: reports.filter(r => r.conditionState === 'CRITICAL'),
    MINOR:    reports.filter(r => r.conditionState === 'MINOR'),
    NORMAL:   reports.filter(r => r.conditionState === 'NORMAL'),
  }

  const conditionLabels: Record<ConditionState, string> = {
    CRITICAL: t('admin.order_detail.condition_critical'),
    MINOR:    t('admin.order_detail.condition_minor'),
    NORMAL:   t('admin.order_detail.condition_normal'),
  }

  const [expanded, setExpanded] = useState<Record<ConditionState, boolean>>({
    CRITICAL: true,
    MINOR: true,
    NORMAL: false,
  })

  const toggle = (state: ConditionState) =>
    setExpanded(prev => ({ ...prev, [state]: !prev[state] }))

  return (
    <div className="admin-detail-card">
      <p className="admin-detail-card__title">
        {t('admin.order_detail.card_diagnostic', { count: reports.length })}
      </p>
      {(['CRITICAL', 'MINOR', 'NORMAL'] as ConditionState[]).map(state => {
        const items = grouped[state]
        if (items.length === 0) return null
        const color = CONDITION_COLORS[state]
        const label = conditionLabels[state]
        const isOpen = expanded[state]
        return (
          <div key={state} className="diag-group">
            <button
              className="diag-group__header"
              onClick={() => toggle(state)}
            >
              <span className="diag-group__dot" style={{ background: color }} />
              <span className="diag-group__label" style={{ color }}>{label}</span>
              <span className="diag-group__count">{items.length}</span>
              <span className={`diag-group__chevron${isOpen ? ' diag-group__chevron--open' : ''}`}>▾</span>
            </button>
            {isOpen && (
              <div className="diag-group__items">
                {items.map(r => (
                  <div key={r.id} className="diag-item">
                    <span className="diag-item__name">{r.name}</span>
                    {r.condition && (
                      <span className="diag-item__condition">{r.condition}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Field({ label, value, highlight }: { label: string; value?: string | null; highlight?: 'ok' | 'error' }) {
  const color = highlight === 'ok' ? '#10b981' : highlight === 'error' ? '#ef4444' : undefined
  return (
    <div className="admin-detail-field">
      <span className="admin-detail-field__label">{label}</span>
      <span className="admin-detail-field__value" style={color ? { color } : undefined}>
        {value || '—'}
      </span>
    </div>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function frameUrl(s3Key: string): string {
  const s3Base = import.meta.env.VITE_S3_BASE_URL ?? 'https://verrafiedfs.s3.amazonaws.com'
  return `${s3Base}/${s3Key}`
}

function audioUrl(orderNumber: string): string {
  const s3Base = import.meta.env.VITE_S3_BASE_URL ?? 'https://verrafiedfs-data.s3.amazonaws.com'
  return `${s3Base}/videos/ml/${orderNumber}_meta/${orderNumber}.mp3`
}

function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M11 4L6 9L11 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }}>
      <path d="M3 2l9 5-9 5V2z" fill="currentColor" />
    </svg>
  )
}
